import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Modal,
  TextField,
  Button,
} from "@mui/material"
import { useState } from "react"
import { useAppDispatch } from "../../app/hooks"
import { openInvoices } from "../../activeScreenSlice"
import { useRejectInvoice } from "../../hooks"
import {
  ButtonWrapper,
  CheckboxColumn,
  CheckboxWrapper,
  ErrorMessage,
  ModalCard,
  ModalHeader,
  ModalTextMain,
  Spacer,
} from "./RejectModal.styles"

export interface Props {
  open: boolean
  onClose: () => void
  invoiceIds: number[]
  onRejectionSubmitted?: () => void
}

export default function RejectModal(props: Props) {
  const [checkboxStates, setCheckboxStates] = useState({
    NotAnInvoice: false,
    DuplicateInvoice: false,
    ProcessedInErp: false,
    IncorrectBillTo: false,
    ZeroValue: false,
    MissingPO: false,
    ClosedInvalidPO: false,
    MissingInvoiceDetails: false,
  })

  const [textFieldContent, setTextFieldContent] = useState("")
  const [ccEmailAddressString, setCcEmailAddressString] = useState("")
  const [ccEmailAddress, setCcEmailAddress] = useState([])
  const [sendEmailToSupplier, setSendEmailToSupplier] = useState(false)
  const dispatch = useAppDispatch()
  const mutation = useRejectInvoice()

  const handleChange = (event) => {
    const input = event.target.value;
    setCcEmailAddressString(input);
    setCcEmailAddress(
      input
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item !== "")
    );
  }

  function submitRejection() {
    mutation.mutate(
      {
        invoiceIds: props.invoiceIds,
        rejectionDetails: {
          reject_to_supplier: sendEmailToSupplier,
          rejection_reasons: Object.entries(checkboxStates)
            .filter(([key, value]) => value)
            .map(([key]) => key),
          custom_notes: textFieldContent,
          cc_emails: ccEmailAddress,
        },
      },
      {
        onSuccess: () => {
          props.onClose()
          
          if (props.onRejectionSubmitted) {
            props.onRejectionSubmitted()
          }
          dispatch(openInvoices())
        },
      },
    )
  }

  function updateCheckbox(
    checkbox: string,
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    setCheckboxStates((prevState) => {
      return {
        ...prevState,
        [checkbox]: event.target.checked,
      }
    })
  }

  function shouldDisableSubmit() {
    if (textFieldContent !== "") {
      return false
    }
    for (const value of Object.values(checkboxStates)) {
      if (value) {
        return false
      }
    }
    return true
  }

  return (
    <Modal open={props.open} onClose={props.onClose}>
      <ModalCard>
        <ModalHeader>
          Please select the reason of invoice rejection from the list below
        </ModalHeader>
        <ModalTextMain>
          Rejected invoices will disappear from the work lists but will stay in
          the system
        </ModalTextMain>
        {mutation.isError && (
          <ErrorMessage>
            An error occurred when rejecting this invoice:{" "}
            {mutation.error.message} - {mutation.error.name}
          </ErrorMessage>
        )}
        <CheckboxWrapper>
          <CheckboxColumn>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxStates.NotAnInvoice}
                    onChange={(event) => updateCheckbox("NotAnInvoice", event)}
                  />
                }
                label="Not an invoice, i.e. PO copy etc"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxStates.DuplicateInvoice}
                    onChange={(event) =>
                      updateCheckbox("DuplicateInvoice", event)
                    }
                  />
                }
                label="Duplicate invoice"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxStates.ProcessedInErp}
                    onChange={(event) =>
                      updateCheckbox("ProcessedInErp", event)
                    }
                  />
                }
                label="Processed in ERP"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxStates.IncorrectBillTo}
                    onChange={(event) =>
                      updateCheckbox("IncorrectBillTo", event)
                    }
                  />
                }
                label="Incorrect bill to"
              />
            </FormGroup>
          </CheckboxColumn>
          <CheckboxColumn>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxStates.ZeroValue}
                    onChange={(event) => updateCheckbox("ZeroValue", event)}
                  />
                }
                label="Zero value invoice"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxStates.MissingPO}
                    onChange={(event) => updateCheckbox("MissingPO", event)}
                  />
                }
                label="Missing PO"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxStates.ClosedInvalidPO}
                    onChange={(event) =>
                      updateCheckbox("ClosedInvalidPO", event)
                    }
                  />
                }
                label="Closed or invalid PO"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxStates.MissingInvoiceDetails}
                    onChange={(event) =>
                      updateCheckbox("MissingInvoiceDetails", event)
                    }
                  />
                }
                label="Missing invoice details"
              />
            </FormGroup>
          </CheckboxColumn>
        </CheckboxWrapper>
        <TextField
          label={"Other reasons"}
          value={textFieldContent}
          onChange={(event) => setTextFieldContent(event.target.value)}
          fullWidth
        />
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmailToSupplier}
                onChange={(event) =>
                  setSendEmailToSupplier(event.target.checked)
                }
                 disabled={checkboxStates.ProcessedInErp}
              />
            }
            label={"Send rejection letter"}
          />
        </FormGroup>
        {sendEmailToSupplier && <TextField
            label={"(optional) CC email addresses separated by comma"}
            value={ccEmailAddressString}
            onChange={handleChange}
            fullWidth
        />}
        <ButtonWrapper>
          <Spacer />
          <Button
              variant="outlined"
              onClick={props.onClose}
          >
              Cancel
          </Button>
          <Button
              variant="contained"
              onClick={submitRejection}
              disabled={shouldDisableSubmit() || mutation.isPending}
          >
              Reject
          </Button>
        </ButtonWrapper>
      </ModalCard>
    </Modal>
  )
}
